import React, { useState, useMemo } from 'react';
import { VehicleBooking } from '../../types/vehicle';
import { Timestamp } from 'firebase/firestore';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  format, 
  addMonths,
  subMonths,
  differenceInDays,
  isWithinInterval,
  startOfDay,
  isSameDay
} from 'date-fns';
import { hu } from 'date-fns/locale';

interface BookingsGanttProps {
  bookings: VehicleBooking[];
  onBookingClick?: (booking: VehicleBooking) => void;
  onBookingDelete?: (bookingId: string, bookingInfo: string) => void;
}

const BookingsGantt: React.FC<BookingsGanttProps> = ({ bookings, onBookingClick, onBookingDelete }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const toDate = (date: Date | Timestamp): Date => {
    return date instanceof Timestamp ? date.toDate() : date;
  };

  // Hónap napjai
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Autók csoportosítása
  const vehicleGroups = useMemo(() => {
    const groups = new Map<string, { 
      vehicleId: string;
      licensePlate: string;
      make: string;
      model: string;
      bookings: VehicleBooking[];
    }>();

    bookings.forEach(booking => {
      const key = booking.vehicleId || 'unknown';
      if (!groups.has(key)) {
        groups.set(key, {
          vehicleId: booking.vehicleId,
          licensePlate: booking.vehicleLicensePlate || '',
          make: booking.vehicleMake || '',
          model: booking.vehicleModel || '',
          bookings: []
        });
      }
      groups.get(key)!.bookings.push(booking);
    });

    // Rendezés rendszám szerint
    return Array.from(groups.values()).sort((a, b) => 
      (a.licensePlate || a.make).localeCompare(b.licensePlate || b.make)
    );
  }, [bookings]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500 hover:bg-yellow-600';
      case 'confirmed':
        return 'bg-green-500 hover:bg-green-600';
      case 'in_progress':
        return 'bg-blue-500 hover:bg-blue-600';
      case 'completed':
        return 'bg-gray-400 hover:bg-gray-500';
      case 'cancelled':
        return 'bg-red-500 hover:bg-red-600';
      default:
        return 'bg-gray-500 hover:bg-gray-600';
    }
  };

  const calculateBookingPosition = (booking: VehicleBooking) => {
    const start = startOfDay(toDate(booking.startDate));
    const end = startOfDay(toDate(booking.endDate));
    
    // Ha a foglalás a hónap előtt kezdődik
    const displayStart = start < monthStart ? monthStart : start;
    // Ha a foglalás a hónap után ér véget
    const displayEnd = end > monthEnd ? monthEnd : end;
    
    const startOffset = differenceInDays(displayStart, monthStart);
    const duration = differenceInDays(displayEnd, displayStart) + 1;
    
    return {
      left: `${(startOffset / days.length) * 100}%`,
      width: `${(duration / days.length) * 100}%`,
      isPartial: start < monthStart || end > monthEnd
    };
  };

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const handleToday = () => {
    setCurrentMonth(new Date());
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TruckIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Foglalások - Gantt nézet
            </h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {format(currentMonth, 'yyyy. MMMM', { locale: hu })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleToday}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Ma
            </button>
            <button
              onClick={handlePrevMonth}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button
              onClick={handleNextMonth}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Gantt Chart */}
      <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          {/* Timeline Header */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="w-48 flex-shrink-0 p-3 font-semibold text-sm text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
              Jármű
            </div>
            <div className="flex-1 flex">
              {days.map((day, index) => {
                const isToday = isSameDay(day, new Date());
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                
                return (
                  <div
                    key={index}
                    className={`flex-1 min-w-[30px] p-2 text-center border-r border-gray-200 dark:border-gray-700 ${
                      isToday ? 'bg-blue-100 dark:bg-blue-900/30' : ''
                    } ${isWeekend ? 'bg-gray-100 dark:bg-gray-800/50' : ''}`}
                  >
                    <div className={`text-xs font-medium ${
                      isToday 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {format(day, 'd')}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-500">
                      {format(day, 'EEE', { locale: hu })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Vehicle Rows */}
          {vehicleGroups.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              Nincs foglalás ebben a hónapban
            </div>
          ) : (
            vehicleGroups.map((vehicle, vehicleIndex) => (
              <div
                key={vehicle.vehicleId || vehicleIndex}
                className="flex border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors"
              >
                {/* Vehicle Info */}
                <div className="w-48 flex-shrink-0 p-3 border-r border-gray-200 dark:border-gray-700">
                  <div className="font-semibold text-sm text-gray-900 dark:text-white">
                    {vehicle.licensePlate || `${vehicle.make} ${vehicle.model}`}
                  </div>
                  {vehicle.licensePlate && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {vehicle.make} {vehicle.model}
                    </div>
                  )}
                </div>

                {/* Timeline */}
                <div className="flex-1 relative" style={{ minHeight: '60px' }}>
                  {/* Background grid */}
                  <div className="absolute inset-0 flex">
                    {days.map((day, index) => {
                      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                      const isToday = isSameDay(day, new Date());
                      
                      return (
                        <div
                          key={index}
                          className={`flex-1 border-r border-gray-200 dark:border-gray-700 ${
                            isToday ? 'bg-blue-50 dark:bg-blue-900/10' : ''
                          } ${isWeekend ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}
                        />
                      );
                    })}
                  </div>

                  {/* Bookings */}
                  {vehicle.bookings.map((booking, bookingIndex) => {
                    const position = calculateBookingPosition(booking);
                    const start = toDate(booking.startDate);
                    const end = toDate(booking.endDate);
                    
                    return (
                      <div
                        key={booking.id}
                        className="absolute top-2 h-10"
                        style={{
                          left: position.left,
                          width: position.width,
                          zIndex: 10 + bookingIndex
                        }}
                      >
                        <div
                          onClick={() => onBookingClick?.(booking)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            if (onBookingDelete) {
                              onBookingDelete(
                                booking.id,
                                `${vehicle.licensePlate || `${vehicle.make} ${vehicle.model}`} - ${booking.userName} (${format(start, 'MMM dd', { locale: hu })} - ${format(end, 'MMM dd', { locale: hu })})`
                              );
                            }
                          }}
                          className={`h-full rounded px-2 py-1 cursor-pointer transition-all shadow-sm ${getStatusColor(booking.status)} text-white text-xs font-medium flex items-center justify-center ${
                            position.isPartial ? 'border-2 border-dashed border-white' : ''
                          }`}
                          title={`🚗 ${vehicle.licensePlate || `${vehicle.make} ${vehicle.model}`}\n👤 ${booking.userName}\n📞 ${booking.userPhone}\n📧 ${booking.userEmail}\n📅 ${format(start, 'yyyy. MMM dd.', { locale: hu })} - ${format(end, 'yyyy. MMM dd.', { locale: hu })}\n💰 ${booking.totalPrice?.toLocaleString()} ${booking.currency || 'HUF'}\n📍 Átvétel: ${booking.pickupLocation || 'Nincs megadva'}\n📍 Leadás: ${booking.dropoffLocation || 'Nincs megadva'}\n\n💡 Bal klikk = szerkesztés\n🗑️ Jobb klikk = törlés`}
                        >
                          <span className="truncate">
                            {booking.userName}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-yellow-500"></div>
            <span className="text-gray-700 dark:text-gray-300">Függőben</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-green-500"></div>
            <span className="text-gray-700 dark:text-gray-300">Jóváhagyva</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-500"></div>
            <span className="text-gray-700 dark:text-gray-300">Folyamatban</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-gray-400"></div>
            <span className="text-gray-700 dark:text-gray-300">Befejezett</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-red-500"></div>
            <span className="text-gray-700 dark:text-gray-300">Törölve</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-12 h-3 rounded bg-blue-500 border-2 border-dashed border-gray-400"></div>
            <span className="text-gray-700 dark:text-gray-300">Hónapon túlnyúló</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingsGantt;
